// SHARED sprite renderer — the SINGLE source of truth for HOW a fighter is drawn,
// used by BOTH the game battle renderer (fighters.ts) and the skin-editor previews.
// Framework-free (canvas 2D only, no app imports) so it is copied VERBATIM into the
// editor by scripts/sync-renderer.sh — edit THIS file, never the editor copy.
//
// Every fighter is a split body: static legs + an upper that leans/rotates with the aim
// + a weapon layered onto the hand; the legs play the 4-frame run cycle while moving.

export interface RSprite {
  canvas: CanvasImageSource;
  // optional source rect of the sprite within `canvas` (a shared atlas image). Absent → the
  // whole `canvas` is the sprite (per-file / editor path). Present → drawn with 9-arg drawImage.
  sx?: number;
  sy?: number;
  sw?: number;
  sh?: number;
  bx: number;
  by: number;
  bw: number;
  bh: number;
}

// draw a sprite's FULL rect at (dx,dy) at natural size. Atlas sprites blit their source rect;
// per-file/editor sprites fall back to the 3-arg whole-image draw — identical pixels either way.
function blit(ctx: CanvasRenderingContext2D, s: RSprite, dx: number, dy: number): void {
  if (s.sx != null && s.sw != null && s.sh != null) {
    ctx.drawImage(s.canvas, s.sx, s.sy ?? 0, s.sw, s.sh, dx, dy, s.sw, s.sh);
  } else {
    ctx.drawImage(s.canvas, dx, dy);
  }
}
export type RStance = 'rifle' | 'awp';
export interface RXY {
  x: number;
  y: number;
}
export interface RUp {
  x: number;
  y: number;
  scale: number;
}
export interface RRun {
  up: RUp;
  scale: number;
}
export interface RAnchors {
  hand: Record<RStance, RXY>;
  up: Record<RStance, RUp>;
  pivot: Record<RStance, RXY>;
  scale: number;
  // whole-body size multiplier PER STANCE for the aim/static animation (scales legs
  // + upper together). The run animation has its own body size in run[stance].scale.
  body: Record<RStance, number>;
  // run rig is PER STANCE — the awp upper differs from the rifle upper, so each
  // needs its own join + body size while running.
  run: Record<RStance, RRun>;
}

// weapon sizing/grip + aim-lean — identical to spriteLayout.ts / editor anchors.ts
export const WEAPON_REL_W = 0.744; // rifle/lmg width as fraction of body standing height
export const AWP_REL_W = 1.1115; // AWP is longer
export const WEAPON_GRIP = { x: 0.34, y: 0.52 }; // grip point inside the weapon content box
export const UPPER_AIM_LEAN = 0.75; // fraction of the aim pitch the upper body leans
export const RUN_FPS = 4;

const relWOf = (st: RStance) => (st === 'awp' ? AWP_REL_W : WEAPON_REL_W);
// Knife held in-hand during a knife rush — its own width + grip point (the
// karambit's finger-ring/handle), independent of the gun anchors.
const KNIFE_REL_W = 0.315;
const KNIFE_GRIP = { x: 0.66, y: 0.72 };

export interface FighterParts {
  legs: RSprite; // static legs (also the run-cycle fallback)
  upper: RSprite; // upper for the current stance
  runFrames?: RSprite[] | null; // legs-run frames (pre-sliced, equal size)
  weapon: RSprite;
}

export interface FighterDraw {
  parts: FighterParts;
  A: RAnchors;
  stance: RStance;
  x: number; // feet x (ground contact)
  y: number; // feet y
  tokenH: number; // target on-screen standing height in px (already × per-skin scale)
  dir?: 1 | -1; // facing (1 = right)
  aim?: number; // radians; weapon angle + upper lean
  bob?: number; // vertical body bob (walking)
  sway?: number; // body sway radians (walking)
  recoilX?: number; // horizontal recoil offset
  moving?: boolean; // play the run cycle
  reverseRun?: boolean; // backpedalling — play the run frames in reverse order
  time?: number; // seconds — drives the run cycle
  muzzle?: RXY; // per-weapon muzzle point (fraction of the weapon content box); default = tip
  knife?: boolean; // holding the knife (knife rush) — draw the knife sprite with its own grip/width
  knifeSwing?: number; // extra local rotation (radians) on the knife — the wind-up/slash arc at the strike
}

function runFrameOf(d: FighterDraw): RSprite {
  const frames = d.parts.runFrames;
  if (!frames || frames.length === 0) return d.parts.legs;
  let idx = Math.floor((d.time ?? 0) * RUN_FPS) % frames.length;
  if (d.reverseRun) idx = frames.length - 1 - idx; // backpedal: cycle runs in reverse
  return frames[idx] ?? frames[0];
}

// on-screen standing height (px) of a split body, for the fit scale + weapon size.
function splitStandingPx(legs: RSprite, upper: RSprite, up: RUp, pivot: RXY): number {
  return up.y * legs.bh + pivot.y * upper.bh * up.scale;
}

// aim relative to the fighter's facing (a horizontal duel puts the enemy at ±π, so
// the raw aim would over-lean the body — only the ELEVATION off the facing axis
// should tilt it). dir=-1 flips the whole body via a mirror frame, so all internal
// math is "facing right" using this local aim.
const normAngle = (a: number) => {
  const t = ((a + Math.PI) % (2 * Math.PI) + 2 * Math.PI) % (2 * Math.PI);
  return t - Math.PI;
};
const localAimOf = (aim: number, dir: 1 | -1) => normAngle(dir === 1 ? aim : Math.PI - aim);

export interface FighterGeom {
  scale: number; // sprite-px → screen-px
  legs: RSprite;
  upper: RSprite;
  body: RSprite; // the legs (for shadow/label sizing)
  us: number; // upper draw scale
  join: RXY; // upper↔legs seam — LOCAL (facing-right) coords
  handPt: RXY; // weapon grip — LOCAL (facing-right) coords
  weaponAngle: number; // radians the weapon is rotated by (local)
  weaponSizeRef: number; // screen-px reference the weapon width scales from
  upperTilt: number;
  pivot: RXY;
  muzzle: RXY; // WORLD muzzle point (bullet exit) — matches where the weapon is actually drawn
}

// pure geometry — everything both the body pass and the (later) weapon pass need,
// so the two never drift. Coords are LOCAL (facing right); the draw fns wrap them in
// a translate(x,y)·scale(dir,1) mirror frame.
export function fighterGeometry(d: FighterDraw): FighterGeom {
  const dir = d.dir ?? 1;
  const aim = d.aim ?? 0;
  const stance = d.stance;
  const hand = d.A.hand[stance];

  const runMode = !!d.moving;
  const legs = runMode ? runFrameOf(d) : d.parts.legs;
  const upper = d.parts.upper;
  const runRig = d.A.run[stance];
  const up = runMode ? runRig.up : d.A.up[stance];
  const pivot = d.A.pivot[stance];
  const standingPx = splitStandingPx(legs, upper, up, pivot);
  // whole-body size for this animation type: run uses run[stance].scale, aim uses body[stance]
  const bodyMul = runMode ? runRig.scale : d.A.body[stance];
  const scale = (d.tokenH / standingPx) * bodyMul;
  const us = scale * up.scale;
  const localAim = localAimOf(aim, dir);
  const upperTilt = UPPER_AIM_LEAN * localAim;

  // idle breathing: while standing (not running), the upper body rises/falls a hair over
  // the grounded legs — a slow ~0.25 Hz sine, desynced per fighter by its x so a row doesn't
  // breathe in unison. The hand (and thus weapon) share `join`, so they breathe in lockstep.
  const breath = runMode || d.time == null ? 0 : Math.sin(d.time * 1.6 + d.x * 0.03) * 0.02 * legs.bh * scale;
  const joinX = up.x * legs.bw * scale;
  const joinY = -up.y * legs.bh * scale - breath;
  const hlx = (hand.x - pivot.x) * upper.bw * us;
  const hly = (hand.y - pivot.y) * upper.bh * us;
  const cu = Math.cos(upperTilt);
  const su = Math.sin(upperTilt);
  const handPt = { x: joinX + (hlx * cu - hly * su), y: joinY + (hlx * su + hly * cu) };

  // muzzle (bullet exit) in WORLD coords — via the SAME transform drawFighterWeapon uses:
  // weapon drawn at handPt, rotated by localAim, y-flipped on rear aim, scaled rs, grip at
  // origin. So the flash/tracer always start at the drawn barrel tip and honour the per-weapon
  // muzzle anchor.
  const weapon = d.parts.weapon;
  const rs = (standingPx * scale * relWOf(stance)) / weapon.bw;
  const mz = d.muzzle ?? { x: 1, y: WEAPON_GRIP.y };
  const mox = (mz.x - WEAPON_GRIP.x) * weapon.bw * rs;
  let moy = (mz.y - WEAPON_GRIP.y) * weapon.bh * rs;
  const mc = Math.cos(localAim);
  const ms = Math.sin(localAim);
  if (mc < 0) moy = -moy; // matches the rear-aim vertical flip in drawFighterWeapon
  const mlx = handPt.x + (mox * mc - moy * ms);
  const mly = handPt.y + (mox * ms + moy * mc);

  return {
    scale,
    legs,
    upper,
    body: legs,
    us,
    join: { x: joinX, y: joinY },
    handPt,
    weaponAngle: localAim,
    weaponSizeRef: standingPx * scale,
    upperTilt,
    pivot,
    muzzle: { x: d.x + dir * mlx, y: d.y + mly },
  };
}

// body pass — legs + upper. NO weapon (that is a later layer so a neighbour's body
// never clips a gun).
export function drawFighterBody(ctx: CanvasRenderingContext2D, d: FighterDraw, g = fighterGeometry(d)): void {
  const dir = d.dir ?? 1;
  const { legs, upper } = g;
  ctx.save();
  ctx.translate(d.x, d.y);
  ctx.scale(dir, 1); // mirror frame: everything below is facing-right
  ctx.save();
  ctx.scale(g.scale, g.scale);
  blit(ctx, legs, -legs.bw / 2, -legs.bh);
  ctx.restore();
  ctx.translate(g.join.x, g.join.y);
  ctx.rotate(g.upperTilt);
  ctx.scale(g.us, g.us);
  blit(ctx, upper, -g.pivot.x * upper.bw, -g.pivot.y * upper.bh);
  ctx.restore();
}

// weapon pass — draws the gun at the hand point (same mirror frame as the body). The
// gun flips vertically when the aim points to the rear so it never hangs upside-down.
export function drawFighterWeapon(ctx: CanvasRenderingContext2D, d: FighterDraw, g = fighterGeometry(d)): void {
  const dir = d.dir ?? 1;
  const weapon = d.parts.weapon;
  const relW = d.knife ? KNIFE_REL_W : relWOf(d.stance);
  const grip = d.knife ? KNIFE_GRIP : WEAPON_GRIP;
  const rs = (g.weaponSizeRef * relW) / weapon.bw;
  ctx.save();
  ctx.translate(d.x, d.y);
  ctx.scale(dir, 1); // same mirror frame as the body
  ctx.translate(g.handPt.x, g.handPt.y);
  ctx.rotate(g.weaponAngle);
  if (d.knife && d.knifeSwing) ctx.rotate(d.knifeSwing);
  if (Math.cos(g.weaponAngle) < 0) ctx.scale(1, -1);
  ctx.scale(rs, rs);
  blit(ctx, weapon, -(weapon.bx + weapon.bw * grip.x), -(weapon.by + weapon.bh * grip.y));
  ctx.restore();
}

// convenience for the editor previews: body + weapon in one call (single layer).
export function drawFighter(ctx: CanvasRenderingContext2D, d: FighterDraw): FighterGeom {
  const g = fighterGeometry(d);
  drawFighterBody(ctx, d, g);
  drawFighterWeapon(ctx, d, g);
  return g;
}
